/**
 * Balance Cache Service
 * Guarda y carga el balance total USD de todas las redes en localStorage,
 * por wallet — permite mostrar el último valor conocido de ESA wallet
 * mientras se recalcula en background, sin filtrar el total de otra wallet.
 */

const CACHE_KEY_PREFIX = 'omdb_wallet_global_balance_usd_';
const CACHE_TIMESTAMP_KEY_PREFIX = 'omdb_wallet_global_balance_timestamp_';

interface CachedBalance {
  value: number;
  timestamp: number;
}

/**
 * Obtiene el balance global cacheado de una wallet
 * @returns El balance guardado o null si no existe
 */
export function getCachedGlobalBalance(address: string): number | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY_PREFIX + address.toLowerCase());
    if (!cached) return null;
    const value = parseFloat(cached);
    return isNaN(value) ? null : value;
  } catch {
    return null;
  }
}

/**
 * Guarda el balance global de una wallet en caché
 * @param address - Dirección de la wallet a la que pertenece este total
 * @param value - El valor USD total
 */
export function setCachedGlobalBalance(address: string, value: number): void {
  try {
    const key = address.toLowerCase();
    localStorage.setItem(CACHE_KEY_PREFIX + key, String(value));
    localStorage.setItem(CACHE_TIMESTAMP_KEY_PREFIX + key, String(Date.now()));
  } catch {
    // Silenciosamente ignorar errores de localStorage
  }
}

/**
 * Obtiene la información completa del caché (valor + timestamp) de una wallet
 */
export function getCachedBalanceInfo(address: string): CachedBalance | null {
  const value = getCachedGlobalBalance(address);
  if (value === null) return null;

  const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY_PREFIX + address.toLowerCase());
  return {
    value,
    timestamp: timestamp ? parseInt(timestamp) : 0,
  };
}

/**
 * Limpia el caché de una wallet
 */
export function clearGlobalBalanceCache(address: string): void {
  try {
    const key = address.toLowerCase();
    localStorage.removeItem(CACHE_KEY_PREFIX + key);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY_PREFIX + key);
  } catch {
    // Silenciosamente ignorar errores
  }
}

export default {
  getCachedGlobalBalance,
  setCachedGlobalBalance,
  getCachedBalanceInfo,
  clearGlobalBalanceCache,
};
